import React, { useContext, useMemo } from 'react';
import _ from 'lodash';
import {
  FeedQuote,
  IFeedSpread,
  ConnectionStatusType,
  FeedOrderUpdate,
  FeedSettlement,
  FeedSpread,
  FeedCashieringUpdate,
  FeedData,
} from '@tradingblock/types';
import { FeedProviderStatus, SymbolChannels } from '../../types';

export interface TradingblockWebsocketState {
  status?: FeedProviderStatus;
  newsStatus?: FeedProviderStatus;
  webSocketCloseReason?: string;
  newsWebSocketCloseReason?: string;
  spreads: { [key: string]: FeedSpread };
  quotes: { [key: string]: FeedQuote };
  quoteQueue: FeedQuote[];
  spreadQueue: FeedSpread[];
  newsSubscriptions: SymbolChannels[];
  orders: {
    [orderId: string]: { status: FeedOrderUpdate; history: FeedOrderUpdate[] };
  };
  cashieringUpdates: { latestUpdateData: FeedData<FeedCashieringUpdate> | null };
  lastMessage: string;
}

/// Redux state object that we use to create data for FeedDataState
// List<Spread> req = new List<Spread>();

//             Spread spread = new Spread();
//             spread.Type = SpreadType.Vertical;
//             spread.Legs = new Leg[2];

//             Leg leg = new Leg();
//             leg.Symbol = ".IWM 190215C134000";
//             leg.AssetType = enumAssetType.Option;

//             spread.Legs[0] = leg;

//             leg = new Leg();
//             leg.Symbol = ".IWM 190215C139000";
//             leg.AssetType = enumAssetType.Option;

//             spread.Legs[1] = leg;

//             req.Add(spread);

export interface FeedState {
  quotes: string[];
  spreads: IFeedSpread[];
  connectionStatus?: ConnectionStatusType;
}

export type FeedActions =
  | {
      type: 'setStatus';
      payload: FeedProviderStatus;
    }
  | {
      type: 'setNewsStatus';
      payload: FeedProviderStatus;
    }
  | {
      type: 'setWebSocketCloseReason';
      payload: string;
    }
  | {
      type: 'setNewsWebSocketCloseReason';
      payload: string;
    }
  | {
      type: 'setQuote';
      payload: { quote: FeedQuote; raw: string };
    }
  | {
      type: 'setSpread';
      payload: { spread: FeedSpread; raw: string };
    }
  | {
      type: 'dropFromQueue';
      payload: { count: number };
    }
  | {
      type: 'dropFromSpreadQueue';
      payload: { count: number };
    }
  | {
      type: 'receivedOrderUpdate';
      payload: { order: FeedOrderUpdate; raw: string };
    }
  | {
      type: 'setSettlement';
      payload: { settlement: FeedSettlement; raw: string };
    }
  | {
      type: 'receivedCashieringUpdate';
      payload: { updateData: FeedData<FeedCashieringUpdate>; rawMsg: string };
    }
  | {
      type: 'addNewsSubscriptions';
      payload: { symbols: SymbolChannels[] };
    }
  | {
      type: 'removeNewsSubscriptions';
      payload: { symbols: string[] };
    }
  | {
      type: 'adjustNewsSubscriptions';
      payload: { symbols: SymbolChannels[] };
    };

export const FeedReducer = (state: TradingblockWebsocketState, action: FeedActions): TradingblockWebsocketState => {
  switch (action.type) {
    case 'setStatus':
      return {
        ...state,
        status: action.payload,
      };
    case 'setNewsStatus':
      return {
        ...state,
        newsStatus: action.payload,
      };
    case 'setWebSocketCloseReason':
      return {
        ...state,
        webSocketCloseReason: action.payload,
      };
    case 'setNewsWebSocketCloseReason':
      return {
        ...state,
        newsWebSocketCloseReason: action.payload,
      };
    case 'dropFromQueue': {
      return {
        ...state,
        quoteQueue: _.drop(state.quoteQueue, action.payload.count),
      };
    }
    case 'dropFromSpreadQueue': {
      return {
        ...state,
        spreadQueue: _.drop(state.spreadQueue, action.payload.count),
      };
    }
    case 'setQuote': {
      const { quote, raw } = action.payload;
      const existingQuote = state.quotes[quote.Symbol] || {};
      const combinedQuoteValue = _.mergeWith({}, existingQuote, quote, (a, b) =>
        _.isNil(b) || _.isNaN(b) ? a : undefined
      );
      return {
        ...state,
        quotes: {
          ...state.quotes,
          [quote.Symbol]: combinedQuoteValue,
        },
        quoteQueue: [...state.quoteQueue, combinedQuoteValue],
        lastMessage: raw,
      };
    }
    case 'setSpread': {
      const { spread, raw } = action.payload;
      const existingSpread = state.spreads[spread.Symbol] || {};
      const combinedSpreadValue = _.mergeWith({}, existingSpread, spread, (a, b) =>
        _.isNil(b) || _.isNaN(b) ? a : undefined
      );
      return {
        ...state,
        spreads: {
          ...state.spreads,
          [spread.Symbol]: combinedSpreadValue,
        },
        spreadQueue: [...state.spreadQueue, combinedSpreadValue],
        lastMessage: raw,
      };
    }
    case 'setSettlement': {
      const { settlement, raw } = action.payload;
      const existingQuote = state.quotes[settlement.Symbol] || {};
      const combinedQuoteValue = _.mergeWith({}, existingQuote, settlement, (a, b) =>
        _.isNil(b) || _.isNaN(b) ? a : undefined
      );
      // Set the quotes Last Trade Price to be from the settlement close price sent over
      combinedQuoteValue.LastTradePrice = settlement.ClosePrice;
      return {
        ...state,
        quotes: {
          ...state.quotes,
          [settlement.Symbol]: combinedQuoteValue,
        },
        quoteQueue: [...state.quoteQueue, combinedQuoteValue],
        lastMessage: raw,
      };
    }
    case 'receivedOrderUpdate': {
      const { order, raw } = action.payload;
      const { OrderId } = order;
      const existing = state.orders[OrderId] || { status: order, history: [] };
      return {
        ...state,
        orders: {
          ...state.orders,
          [OrderId]: {
            ...existing,
            status: order,
            history: [...existing.history, order],
          },
        },
        lastMessage: raw,
      };
    }
    case 'receivedCashieringUpdate': {
      const { updateData, rawMsg } = action.payload;

      return {
        ...state,
        cashieringUpdates: {
          latestUpdateData: updateData,
        },
        lastMessage: rawMsg,
      };
    }
    case 'addNewsSubscriptions': {
      const { symbols } = action.payload;
      const updatedSubscriptions = mergeArrays(state.newsSubscriptions, symbols);
      return {
        ...state,
        newsSubscriptions: [...updatedSubscriptions],
      };
    }
    case 'removeNewsSubscriptions': {
      const { symbols } = action.payload;
      const updatedSubscriptions = state.newsSubscriptions.filter(s => !symbols.includes(s.name));
      return {
        ...state,
        newsSubscriptions: [...updatedSubscriptions],
      };
    }
    case 'adjustNewsSubscriptions': {
      const { symbols } = action.payload;
      const adjustedSubscriptions = state.newsSubscriptions.map(s => {
        const foundSymbol = symbols.find(fs => fs.name === s.name);
        if (foundSymbol) return foundSymbol;
        else return s;
      });
      return {
        ...state,
        newsSubscriptions: [...adjustedSubscriptions],
      };
    }

    default:
      return state;
  }
};

function mergeArrays(arr1: SymbolChannels[], arr2: SymbolChannels[]) {
  const map = new Map();

  // Function to add items to the map
  const addToMap = (item: any) => {
    if (!map.has(item.name)) {
      map.set(item.name, { ...item });
    } else {
      map.get(item.name).channels = [...new Set([...map.get(item.name).channels, ...item.channels])];
    }
  };

  // Add items from both arrays to the map
  arr1.forEach(addToMap);
  arr2.forEach(addToMap);

  // Convert the map back to an array
  return Array.from(map.values());
}

export const FeedInitialState: TradingblockWebsocketState = {
  quoteQueue: [],
  spreadQueue: [],
  spreads: {},
  quotes: {},
  orders: {},
  lastMessage: '',
  newsSubscriptions: [],
  cashieringUpdates: { latestUpdateData: null },
};

export const FeedActionContext = React.createContext<React.Dispatch<FeedActions>>(() => {});
export const FeedStateContext = React.createContext<TradingblockWebsocketState>(FeedInitialState);

export const getFeedActions = (dispatch: React.Dispatch<FeedActions>) => {
  const setStatus = (status: FeedProviderStatus) => dispatch({ type: 'setStatus', payload: status });
  const setNewsStatus = (status: FeedProviderStatus) => dispatch({ type: 'setNewsStatus', payload: status });
  const setCloseReason = (reason: string) => dispatch({ type: 'setWebSocketCloseReason', payload: reason });
  const setNewsCloseReason = (reason: string) => dispatch({ type: 'setNewsWebSocketCloseReason', payload: reason });
  return {
    setStatus,
    setNewsStatus,
    setCloseReason,
    setNewsCloseReason,
    onQuote: (quote: FeedQuote, raw: string) => dispatch({ type: 'setQuote', payload: { quote, raw } }),
    onSpread: (spread: FeedSpread, raw: string) => dispatch({ type: 'setSpread', payload: { spread, raw } }),
    onOrderUpdate: (order: FeedOrderUpdate, raw: string) =>
      dispatch({ type: 'receivedOrderUpdate', payload: { order, raw } }),
    onSettlement: (settlement: FeedSettlement, raw: string) =>
      dispatch({ type: 'setSettlement', payload: { settlement, raw } }),
    dropFromQueue: (count: number) => dispatch({ type: 'dropFromQueue', payload: { count } }),
    dropFromSpreadQueue: (count: number) => dispatch({ type: 'dropFromSpreadQueue', payload: { count } }),
    onCashieringUpdate: (updateData: FeedData<FeedCashieringUpdate>, rawMsg: string) =>
      dispatch({ type: 'receivedCashieringUpdate', payload: { updateData, rawMsg } }),
    addNewsSubscriptions: (symbols: SymbolChannels[]) =>
      dispatch({ type: 'addNewsSubscriptions', payload: { symbols } }),
    removeNewsSubscriptions: (symbols: string[]) => dispatch({ type: 'removeNewsSubscriptions', payload: { symbols } }),
    adjustNewsSubscriptions: (symbols: SymbolChannels[]) =>
      dispatch({ type: 'adjustNewsSubscriptions', payload: { symbols } }),
  };
};

export const useFeedActions = () => {
  const dispatch = useContext(FeedActionContext);
  return useMemo(() => getFeedActions(dispatch), [dispatch]);
};
